import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar2Week,
  ChevronRight,
  Search,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import SubscribeModal from "../../modals/SubscribeModal";
import { useAuthContext } from "../../hooks/useAuthContext";

import "./ClientsPage.css";

export default function ClientsPage({ profile }) {
  const [clients, setClients] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pageProfile, setPageProfile] = useState(profile || null);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { user } = useAuthContext();
  const navigate = useNavigate();

  const currentProfile = profile || pageProfile;

  const activeSubscription =
    currentProfile?.activeSubscription ??
    currentProfile?.ActiveSubscription ??
    false;

  const freeTrialExpiresAt =
    currentProfile?.freeTrialExpiresAt ??
    currentProfile?.FreeTrialExpiresAt ??
    null;

  const freeTrialExpired =
    freeTrialExpiresAt &&
    new Date(freeTrialExpiresAt) < new Date();

  const subscriptionRequired =
    freeTrialExpired === true &&
    activeSubscription === false;

  const isClientArchived = (client) => {
    const archived =
      client.isArchived ??
      client.IsArchived;

    if (
      archived !== undefined &&
      archived !== null
    ) {
      return archived === true;
    }

    const isActive =
      client.isActive ??
      client.IsActive;

    if (
      isActive !== undefined &&
      isActive !== null
    ) {
      return isActive === false;
    }

    return false;
  };

  const activeClientCount = clients.filter(
    (client) => !isClientArchived(client)
  ).length;

  const archivedClientCount =
    clients.filter(isClientArchived).length;

  const nextAppointmentByClient = useMemo(() => {
    const appointmentMap = new Map();
    const now = new Date();

    upcomingAppointments.forEach((appointment) => {
      const clientId =
        appointment.clientId ??
        appointment.ClientId;

      const therapySession =
        appointment.therapySession ??
        appointment.TherapySession ??
        false;

      const start =
        appointment.start ??
        appointment.Start;

      if (
        !clientId ||
        !therapySession ||
        !start
      ) {
        return;
      }

      const startDate = new Date(start);

      // Ignore appointments that have already started.
      if (startDate <= now) {
        return;
      }

      const mapKey = String(clientId);

      const existingAppointment =
        appointmentMap.get(mapKey);

      const existingStart =
        existingAppointment?.start ??
        existingAppointment?.Start;

      if (
        !existingAppointment ||
        startDate < new Date(existingStart)
      ) {
        appointmentMap.set(
          mapKey,
          appointment
        );
      }
    });

    return appointmentMap;
  }, [upcomingAppointments]);

  const visibleClients = useMemo(() => {
    const cleanSearch =
      searchTerm.trim().toLowerCase();

    return clients
      .filter((client) =>
        showArchived
          ? isClientArchived(client)
          : !isClientArchived(client)
      )
      .filter((client) => {
        if (!cleanSearch) {
          return true;
        }

        const firstName =
          client.firstName ??
          client.FirstName ??
          "";

        const lastName =
          client.lastName ??
          client.LastName ??
          "";

        const email =
          client.email ??
          client.Email ??
          "";

        const fullName =
          `${firstName} ${lastName}`.trim();

        return (
          fullName
            .toLowerCase()
            .includes(cleanSearch) ||
          email
            .toLowerCase()
            .includes(cleanSearch)
        );
      })
      .sort((clientA, clientB) => {
        const firstNameA =
          clientA.firstName ??
          clientA.FirstName ??
          "";

        const lastNameA =
          clientA.lastName ??
          clientA.LastName ??
          "";

        const firstNameB =
          clientB.firstName ??
          clientB.FirstName ??
          "";

        const lastNameB =
          clientB.lastName ??
          clientB.LastName ??
          "";

        return `${lastNameA} ${firstNameA}`.localeCompare(
          `${lastNameB} ${firstNameB}`
        );
      });
  }, [clients, showArchived, searchTerm]);

  const currentClientCount =
    showArchived
      ? archivedClientCount
      : activeClientCount;

  const formatAppointmentStart = (value) => {
    if (!value) {
      return "";
    }

    return new Date(value).toLocaleString(
      "en-GB",
      {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const handleAddClient = () => {
    if (subscriptionRequired) {
      setShowSubscribeModal(true);
      return;
    }

    navigate("/clients/create");
  };

  const toggleArchived = () => {
    setShowArchived(
      (current) => !current
    );

    setSearchTerm("");
  };

  useEffect(() => {
    const loadPageData = async () => {
      if (!user?.token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setErr("");

        const requestConfig = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };

        const clientsRequest = axios.get(
          `${ApiConnectionString}/api/Clients`,
          requestConfig
        );

        const upcomingAppointmentsRequest =
          axios.get(
            `${ApiConnectionString}/api/Appointments/upcoming`,
            requestConfig
          );

        const profileRequest = profile
          ? Promise.resolve({
              data: profile,
            })
          : axios.get(
              `${ApiConnectionString}/api/myProfile/`,
              requestConfig
            );

        const [
          clientsResponse,
          upcomingAppointmentsResponse,
          profileResponse,
        ] = await Promise.all([
          clientsRequest,
          upcomingAppointmentsRequest,
          profileRequest,
        ]);

        setClients(
          clientsResponse.data || []
        );

        setUpcomingAppointments(
          upcomingAppointmentsResponse.data ||
            []
        );

        setPageProfile(
          profileResponse.data
        );
      } catch (error) {
        console.error(error);
        setErr("Failed to load clients.");
      } finally {
        setIsLoading(false);
      }
    };

    loadPageData();
  }, [user, profile]);

  if (isLoading) {
    return (
      <div className="clients-loading">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <SubscribeModal
        show={showSubscribeModal}
        onClose={() =>
          setShowSubscribeModal(false)
        }
      />

      <div className="clients-page">
        <div className="clients-header">
          <div>
            <h1>
              {showArchived
                ? "Archived clients"
                : "Clients"}
            </h1>

            <p>
              {showArchived
                ? "View clients that you have archived."
                : "View and manage your therapy clients."}
            </p>
          </div>

          <div className="clients-header-actions">
            <button
              type="button"
              className="clients-secondary-btn"
              onClick={toggleArchived}
            >
              {showArchived
                ? "Back to active clients"
                : `Archived clients${
                    archivedClientCount > 0
                      ? ` (${archivedClientCount})`
                      : ""
                  }`}
            </button>

            {!showArchived && (
              <button
                type="button"
                className="clients-btn"
                onClick={handleAddClient}
              >
                Add client
              </button>
            )}
          </div>
        </div>

        {err && (
          <div className="clients-error alert alert-danger">
            {err}
          </div>
        )}

        <div className="clients-card">
          <div className="clients-toolbar">
            <div className="clients-search">
              <Search />

              <input
                type="search"
                placeholder="Search clients by name or email"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(
                    e.target.value
                  )
                }
                aria-label="Search clients"
              />
            </div>

            <span className="clients-count">
              {currentClientCount}{" "}
              {currentClientCount === 1
                ? "client"
                : "clients"}
            </span>
          </div>

          {visibleClients.length === 0 ? (
            <div className="clients-empty">
              {searchTerm.trim() ? (
                <>
                  <h2>No clients found</h2>

                  <p>
                    Try searching with a
                    different name or email
                    address.
                  </p>

                  <button
                    type="button"
                    className="clients-secondary-btn"
                    onClick={() =>
                      setSearchTerm("")
                    }
                  >
                    Clear search
                  </button>
                </>
              ) : showArchived ? (
                <>
                  <h2>
                    No archived clients
                  </h2>

                  <p>
                    Clients that you archive
                    will appear here.
                  </p>

                  <button
                    type="button"
                    className="clients-secondary-btn"
                    onClick={() =>
                      setShowArchived(false)
                    }
                  >
                    Back to active clients
                  </button>
                </>
              ) : (
                <>
                  <h2>No clients yet</h2>

                  <p>
                    Add your first client to
                    start booking
                    appointments.
                  </p>

                  <button
                    type="button"
                    className="clients-btn"
                    onClick={handleAddClient}
                  >
                    Add client
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="clients-list">
              {visibleClients.map(
                (client) => {
                  const clientId =
                    client.id ??
                    client.Id;

                  const firstName =
                    client.firstName ??
                    client.FirstName ??
                    "";

                  const lastName =
                    client.lastName ??
                    client.LastName ??
                    "";

                  const email =
                    client.email ??
                    client.Email ??
                    "";

                  const emailHidden =
                    client.emailHidden ??
                    client.EmailHidden ??
                    false;

                  const canSelfBook =
                    client.canSelfBookAppointments ??
                    client.CanSelfBookAppointments ??
                    false;

                  const fullName =
                    `${firstName} ${lastName}`.trim() ||
                    "Unnamed client";

                  const nextAppointment =
                    nextAppointmentByClient.get(
                      String(clientId)
                    );

                  const nextAppointmentStart =
                    nextAppointment?.start ??
                    nextAppointment?.Start ??
                    null;

                  return (
                    <Link
                      to={`/client/${clientId}`}
                      className="client-row"
                      key={clientId}
                    >
                      <div className="client-avatar">
                        {(firstName ||
                          "?")[0]}
                        {(lastName ||
                          "")[0]}
                      </div>

                      <div className="client-details">
                        <strong>
                          {fullName}
                        </strong>

                        <span
                          className={
                            email
                              ? "client-email"
                              : "client-email client-email-missing"
                          }
                        >
                          {emailHidden
                            ? "Email hidden"
                            : email ||
                              "Email address missing"}
                        </span>

                        <div
                          className={`client-next-appointment ${
                            nextAppointmentStart
                              ? "has-appointment"
                              : "no-appointment"
                          }`}
                        >
                          <Calendar2Week />

                          <span>
                            {nextAppointmentStart
                              ? `Next appointment: ${formatAppointmentStart(
                                  nextAppointmentStart
                                )}`
                              : "No upcoming appointment"}
                          </span>
                        </div>

                        <div className="client-meta">
                          {canSelfBook &&
                            !showArchived && (
                              <span className="client-status client-status-booking">
                                Self-booking
                                enabled
                              </span>
                            )}

                          {showArchived && (
                            <span className="client-status client-status-archived">
                              Archived
                            </span>
                          )}
                        </div>
                      </div>

                      <span className="client-view">
                        <span>View</span>
                        <ChevronRight />
                      </span>
                    </Link>
                  );
                }
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}